import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Bell, Activity, CheckCircle, XCircle
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { ComprehensiveUserProfile } from './types';

interface ActivityTabProps {
  profile: ComprehensiveUserProfile;
}

export const ActivityTab: React.FC<ActivityTabProps> = ({ profile }) => {
  const { notifications, auditActivity } = profile;

  return (
    <div className="space-y-6">
      {/* Notifications Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-4 rounded-lg bg-muted/50 border border-border/50 text-center">
              <p className="text-3xl font-bold">{notifications?.total || 0}</p>
              <p className="text-sm text-muted-foreground">Total Notifications</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 border border-border/50 text-center">
              <p className="text-3xl font-bold text-orange-500">{notifications?.unread || 0}</p>
              <p className="text-sm text-muted-foreground">Unread</p>
            </div>
          </div>
          
          {notifications?.recent?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground">Recent Notifications</h4>
              {notifications.recent.map((notification, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-muted/50 border border-border/50 flex items-start gap-3">
                  <div className={`mt-0.5 ${notification.isRead ? 'text-muted-foreground' : 'text-primary'}`}>
                    <Bell className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <p className={`text-sm ${notification.isRead ? 'text-muted-foreground' : 'font-medium'}`}>
                        {notification.title}
                      </p>
                      <Badge variant={notification.isRead ? 'secondary' : 'default'} className="text-xs shrink-0">
                        {notification.isRead ? 'Read' : 'Unread'}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <Badge variant="outline" className="text-xs">
                        {notification.type?.replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {notification.createdAt 
                          ? formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })
                          : ''}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit Activity Log */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            Activity Log (Last 20 Actions)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {auditActivity?.length > 0 ? (
            <div className="space-y-2">
              {auditActivity.map((activity, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-muted/50 border border-border/50 flex items-center gap-3">
                  <div className={activity.success ? 'text-green-500' : 'text-destructive'}>
                    {activity.success ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">{activity.action}</span>
                      <span className="text-xs text-muted-foreground">
                        {activity.timestamp 
                          ? format(new Date(activity.timestamp), 'MMM d, h:mm a')
                          : ''}
                      </span>
                    </div>
                    {activity.targetEntityType && (
                      <Badge variant="outline" className="text-xs mt-1">
                        {activity.targetEntityType}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center">No activity logged</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
